import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import BoxHolder from '../../components/shared/BoxHolder/BoxHolder';
import BtButton from '../bt-button/bt-button';
import { ProjectGroup, ProjectGroupSupplier } from '../../services/service-evaluations';
import { EvaluationContextType } from '../../util/evaluationContract';
import { WizardSeed, supplierEvaluationsUrl } from '../../util/evaluationsNavigation';
import RequestLine from './RequestLine';
import UnevaluatedLine, { seedSupplier } from './UnevaluatedLine';
import styles from './app-evaluation-overview.module.scss';

type Props = {
  group: ProjectGroup;
  canWrite: boolean;
  onStart: (seed: WizardSeed) => void;
};

const ProjectGroupPanel: FC<Props> = ({ group, canWrite, onStart }) => {
  const [t] = useTranslation('evaluations');
  const canStart = canWrite && group.canStart && Boolean(group.projectId);
  // The server withholds an own-only group's purchases, so an empty list says nothing.
  const showsPurchases = group.type === EvaluationContextType.Project && !group.ownOnly;

  const start = (supplier: ProjectGroupSupplier, purchases: { areaId: string }[]) =>
    onStart({
      project: { _id: group.projectId as string, name: group.name ?? '', number: group.number },
      suppliers: [seedSupplier({ _id: supplier.supplierId, name: supplier.name }, purchases)],
    });

  const purchaseLine = (supplier: ProjectGroupSupplier) =>
    supplier.purchases.length
      ? t('overview.panel.purchases', 'Inköp: {{names}}', {
          names: supplier.purchases.map(purchase => purchase.name).join(', '),
        })
      : t('overview.panel.notInPurchases', 'Finns inte i projektets inköp');

  return (
    <div className={styles.panel}>
      {group.suppliers.map(supplier => (
        <BoxHolder key={supplier.supplierId} className={styles.block}>
          <div className={styles.blockHead}>
            <div className={styles.blockTitle}>
              <Link className={styles.rowLink} to={supplierEvaluationsUrl(supplier.supplierId)}>
                {supplier.name}
              </Link>
              {showsPurchases && (
                <span className={`${styles.small} ${styles.muted}`}>{purchaseLine(supplier)}</span>
              )}
            </div>
            {canStart && !supplier.unevaluated && !supplier.isDeleted && (
              <div className={styles.blockActions}>
                <BtButton
                  color="white"
                  size="xxs"
                  tabIndex={0}
                  onClick={() => start(supplier, supplier.purchases)}
                >
                  {t('register.newEvaluation', 'Ny utvärdering')}
                </BtButton>
              </div>
            )}
          </div>
          {supplier.unevaluated && (
            <UnevaluatedLine
              title={t('overview.unevaluated.title', {
                count: supplier.unevaluated.purchases.length,
                defaultValue_one: 'Vunnet inköp',
                defaultValue_other: 'Vunna inköp',
              })}
              purchases={supplier.unevaluated.purchases}
              onEvaluate={
                canStart ? () => start(supplier, supplier.unevaluated?.purchases ?? []) : undefined
              }
            />
          )}
          {supplier.requests.map(row => (
            <RequestLine key={row.requestId} row={row} />
          ))}
        </BoxHolder>
      ))}
      {group.rowsCapped && (
        <p className={`${styles.groupCapNote} ${styles.small} ${styles.muted}`}>
          {t('overview.group.capped', 'Visar de {{shown}} senaste av {{total}} utvärderingar.', {
            shown: group.rowsCapped.shown,
            total: group.rowsCapped.total,
          })}
        </p>
      )}
    </div>
  );
};

export default ProjectGroupPanel;
